import {
  CheckIcon as HeroCheckIcon,
  ChevronRightIcon,
  DownloadIcon,
  DuplicateIcon,
  FolderOpenIcon as HeroFolderOpenIcon,
  InformationCircleIcon,
  TrashIcon as HeroTrashIcon,
  XIcon,
} from "@heroicons/react/outline";

interface IconProps {
  className?: string;
}

export const CheckIcon = ({ className = "w-3.5 h-3.5" }: IconProps) => (
  <HeroCheckIcon className={className} strokeWidth={2.5} />
);

export const InfoCircleIcon = ({ className = "w-3.5 h-3.5" }: IconProps) => (
  <InformationCircleIcon className={className} strokeWidth={2} />
);

export const WarningCircleIcon = ({ className = "w-3.5 h-3.5" }: IconProps) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export const DownloadArrowIcon = ({ className = "w-3.5 h-3.5" }: IconProps) => (
  <DownloadIcon className={className} strokeWidth={2} />
);

export const CloseIcon = ({ className = "w-4 h-4" }: IconProps) => (
  <XIcon className={className} strokeWidth={2} />
);

export const DeviceIcon = ({ className = "w-3.5 h-3.5" }: IconProps) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 11V8m0 0l-2 2m2-2l2 2" />
  </svg>
);

/** Android logo — for placeholder/status use */
export const DevicePlainIcon = ({ className = "w-4 h-4" }: IconProps) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48A5.84 5.84 0 0012 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31A5.983 5.983 0 006 7h12c0-2.21-1.2-4.15-2.97-5.19-.01-.02-.01-.04 0-.05zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
  </svg>
);

export const ComputerIcon = ({ className = "w-3.5 h-3.5" }: IconProps) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z"
    />
  </svg>
);

export const TrashIcon = ({ className = "w-3.5 h-3.5" }: IconProps) => (
  <HeroTrashIcon className={className} strokeWidth={2} />
);

export const PlayIcon = ({ className = "w-4 h-4" }: IconProps) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

export const FolderOpenIcon = ({ className = "w-3.5 h-3.5" }: IconProps) => (
  <HeroFolderOpenIcon className={className} strokeWidth={2} />
);

export const ChevronIcon = ({
  className = "w-3.5 h-3.5",
  open = false,
}: IconProps & { open?: boolean }) => (
  <ChevronRightIcon
    className={`${className} transition-transform ${open ? "rotate-90" : ""}`}
    strokeWidth={2}
  />
);

export const CopyIcon = ({ className = "w-4 h-4" }: IconProps) => (
  <DuplicateIcon className={className} strokeWidth={2} />
);

interface SpinnerProps {
  className?: string;
  color?: "green" | "gray";
}

export const Spinner = ({ className = "w-3 h-3", color = "gray" }: SpinnerProps) => (
  <div
    className={`${className} border-2 border-t-transparent rounded-full animate-spin`}
    style={
      color === "green"
        ? { borderColor: "#00c889", borderTopColor: "transparent" }
        : { borderColor: "#d1d5db", borderTopColor: "transparent" }
    }
  />
);
